<script setup lang="ts">
import type {
  ButtonBorderSides,
  InputBlock as InputBlockType,
  ViewportSize,
} from "@aswin.dev/types";
import { computed } from "vue";

const props = defineProps<{
  block: InputBlockType;
  viewport: ViewportSize;
}>();

function spacingCss(s: {
  top: number;
  right: number;
  bottom: number;
  left: number;
}): string {
  return `${s.top}px ${s.right}px ${s.bottom}px ${s.left}px`;
}

function borderCss(
  widthPx: number,
  color: string,
  sides: ButtonBorderSides,
): Record<string, string> {
  if (widthPx <= 0) {
    return { border: "none" };
  }
  const w = `${widthPx}px`;
  const style: Record<string, string> = {
    borderStyle: "solid",
    borderColor: color,
  };
  if (sides === "all") {
    style.borderWidth = w;
    return style;
  }
  const z = "0";
  style.borderTopWidth = sides === "top" ? w : z;
  style.borderRightWidth = sides === "right" ? w : z;
  style.borderBottomWidth = sides === "bottom" ? w : z;
  style.borderLeftWidth = sides === "left" ? w : z;
  return style;
}

const inputDomId = computed(() => `tpl-in-${props.block.id}`);

const labelStyle = computed(
  (): Record<string, string> => ({
    display: "block",
    margin: spacingCss(props.block.labelMargin),
    padding: spacingCss(props.block.labelPadding),
    fontSize: `${props.block.labelFontSize}px`,
    fontWeight: props.block.labelFontWeight,
    color: props.block.labelColor,
    textAlign: props.block.labelTextAlign,
    ...(props.block.labelFontFamily
      ? { fontFamily: props.block.labelFontFamily }
      : {}),
  }),
);

const inputStyle = computed((): Record<string, string> => {
  const bw = props.block.inputBorderWidth ?? 0;
  const sides = props.block.inputBorderSides ?? "all";
  const bc = props.block.inputBorderColor ?? "#d1d5db";
  const border = borderCss(bw, bc, sides);
  const width =
    props.block.inputWidth === "full" ? "100%" : `${props.block.inputWidth}px`;
  return {
    boxSizing: "border-box",
    width,
    margin: spacingCss(props.block.inputMargin),
    padding: spacingCss(props.block.inputPadding),
    backgroundColor: props.block.inputBackgroundColor,
    color: props.block.inputTextColor,
    fontSize: `${props.block.inputFontSize}px`,
    borderRadius: `${props.block.inputBorderRadius}px`,
    outline: "none",
    ...(props.block.inputFontFamily
      ? { fontFamily: props.block.inputFontFamily }
      : {}),
    ...border,
  };
});

const placeholderHex = computed(() => props.block.inputPlaceholderColor);
</script>

<template>
  <div class="tpl-field-root tpl:w-full">
    <label :for="inputDomId" :style="labelStyle">{{ block.label }}</label>
    <input
      :id="inputDomId"
      class="tpl-field-input tpl:block tpl:max-w-full"
      :type="block.inputType"
      :name="block.name"
      :placeholder="block.placeholder"
      :value="block.defaultValue ?? ''"
      :required="block.required"
      readonly
      tabindex="-1"
      :style="inputStyle"
    />
  </div>
</template>

<style scoped>
.tpl-field-root {
  --tpl-input-ph: v-bind(placeholderHex);
}
.tpl-field-input::placeholder {
  color: var(--tpl-input-ph);
  opacity: 1;
}
</style>
